import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { toast } from "react-toastify";
import styles from "../styles/styles";
import { server } from "../server";
import { clearCart } from "../redux/actions/cart";
import {
  clearStripePendingCheckout,
  loadStripePendingCheckout,
  paymentIntentIdFromClientSecret,
  registerStripePendingCheckout,
} from "../utils/stripePendingCheckout";
import { cacheStripeKeyResponse } from "../utils/stripePaymentUi";

/** Browser cache TTL — server basket is kept 90 days; do not block return if local copy expired. */
const MAX_LOCAL_PENDING_MS = 90 * 60 * 1000;

const StripePaymentReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [message, setMessage] = useState("Confirming your payment…");
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fail = (text, goPayment) => {
      if (cancelled) return;
      setMessage(text);
      setBusy(false);
      clearStripePendingCheckout();
      if (goPayment) {
        toast.error(text);
        navigate("/payment", { replace: true });
      }
    };

    const succeed = () => {
      if (cancelled) return;
      setMessage("Order placed successfully. Redirecting…");
      setBusy(false);
      setTimeout(() => navigate("/order/success", { replace: true }), 600);
    };

    const run = async () => {
      const redirectStatus = searchParams.get("redirect_status");
      if (redirectStatus === "failed" || redirectStatus === "canceled") {
        fail(
          "Payment was cancelled or declined. You have not been charged. You can try again with another method.",
          false
        );
        return;
      }

      const clientSecret = searchParams.get("payment_intent_client_secret");
      const paymentIntentId =
        searchParams.get("payment_intent") ||
        paymentIntentIdFromClientSecret(clientSecret || "");

      if (!paymentIntentId && !clientSecret) {
        fail(
          "We could not confirm this payment link. Please go back to checkout and try again.",
          true
        );
        return;
      }

      const rawPending = loadStripePendingCheckout();
      const localStale =
        rawPending?.savedAt &&
        Date.now() - Number(rawPending.savedAt) > MAX_LOCAL_PENDING_MS;
      const pending = localStale ? null : rawPending;

      try {
        let paymentIntent = null;
        if (clientSecret) {
          const { data } = await axios.get(`${server}/payment/stripe/key`, {
            withCredentials: true,
          });
          cacheStripeKeyResponse(data);
          const pk = data?.publishableKey;
          if (!pk) {
            fail("Payment system is not available. Please try again later.", true);
            return;
          }
          const stripe = await loadStripe(pk);
          if (!stripe) {
            fail("Could not load payment confirmation. Please try again.", true);
            return;
          }
          const { paymentIntent: pi, error: retrieveError } =
            await stripe.retrievePaymentIntent(clientSecret);
          if (retrieveError) {
            fail(
              retrieveError.message?.includes("return_url")
                ? "This payment could not be confirmed. Please try again or pay with card."
                : "We could not verify your payment. Please try again from checkout.",
              true
            );
            return;
          }
          paymentIntent = pi;
        }

        if (paymentIntent && paymentIntent.status !== "succeeded") {
          fail(
            `Payment status: ${paymentIntent.status}. If money left your account, contact support with reference ${paymentIntent.id}.`,
            false
          );
          return;
        }

        const piId = paymentIntent?.id || paymentIntentId;
        const checkoutBody = pending?.order
          ? {
              paymentIntentId: piId,
              orderPayload: pending.order,
              orderDataExtras: pending.orderData || null,
              walletAmountUsed: Number(pending.walletAmountUsed || 0),
            }
          : { paymentIntentId: piId };

        if (pending?.order) {
          try {
            await registerStripePendingCheckout({
              server,
              paymentIntentId: piId,
              order: pending.order,
              orderData: pending.orderData,
              walletAmountUsed: pending.walletAmountUsed,
            });
          } catch {
            /* complete-stripe-checkout can still accept orderPayload in body */
          }
        }

        let orderResponse;
        try {
          orderResponse = await axios.post(
            `${server}/order/complete-stripe-checkout`,
            checkoutBody,
            {
              headers: { "Content-Type": "application/json" },
              withCredentials: true,
            }
          );
        } catch (completeErr) {
          if (
            completeErr?.response?.status === 404 &&
            paymentIntent?.status === "succeeded" &&
            !pending?.order
          ) {
            throw new Error(
              "Your payment succeeded but we could not find your checkout basket. " +
                `Please contact support with payment reference: ${piId}`
            );
          }
          throw completeErr;
        }

        const firstOrder = orderResponse.data?.orders?.[0];
        const orderData =
          orderResponse.data?.orderDataExtras ||
          pending?.orderData ||
          {};
        localStorage.setItem(
          "successOrderData",
          JSON.stringify({
            ...orderData,
            paymentInfo: {
              id: piId,
              status: "succeeded",
              type: "Stripe",
            },
            orderId: firstOrder?._id || null,
            orderNumber: firstOrder?.orderNumber ?? null,
          })
        );
        clearStripePendingCheckout();
        dispatch(clearCart());
        localStorage.setItem("latestOrder", JSON.stringify([]));
        toast.success(
          orderResponse.data?.recovered
            ? "Order recovered — thank you for your purchase!"
            : "Order successful!"
        );
        succeed();
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          const returnTo = `/payment/stripe-return${window.location.search}`;
          navigate(`/login?redirect=${encodeURIComponent(returnTo)}`, { replace: true });
          return;
        }
        const apiMsg = err?.response?.data?.message;
        setMessage(
          apiMsg ||
            "Your payment may have succeeded, but we could not place the order. Please contact support before paying again, and mention you used Klarna or card."
        );
        setBusy(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, searchParams]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-8 text-center shadow">
          <h1 className="text-2xl font-semibold text-slate-800">Payment</h1>
          <p className="mt-4 text-sm text-slate-600">{message}</p>
          {busy && (
            <div className="mt-6 flex justify-center">
              <span
                className={`${styles.button} !cursor-default !bg-[#38513B] text-white`}
              >
                Please wait…
              </span>
            </div>
          )}
          {!busy && (
            <button
              type="button"
              className={`${styles.button} mt-6 text-white`}
              onClick={() => navigate("/payment", { replace: true })}
            >
              Back to payment
            </button>
          )}
      </div>
    </div>
  );
};

export default StripePaymentReturn;
